import React, { SyntheticEvent, Component, createRef } from "react";
import { FormContext } from "../form";
import { required, validateInput } from "../utilities/validations";
import Tooltip from "@adeccoux/tag-ds/tooltip";
import WysiwygBarCommands, { ICommandsProps } from "./wsyiwyg-bar-commands";
import { bytesControlCharactersRegExp, htmlExtraSpacesRegExp, htmlOnlyOneExtraSpace, htmlStrangeCharacters, literalControlCharactersRegExp } from "../../../../helper/regexp-helper";
import { getMultilanguageInputName, renderMutilanguageDefaultHiddenInput } from "../multilanguage/multi-language-service";
import DOMPurify from "dompurify";

export interface IProps extends ICommandsProps {
  id?: string;
  name?: string;
  type?: string;
  label?: string;
  tooltip?: string;
  className?: string;
  placeholder?: string;
  value?: string;
  errors?: string[];
  disabled?: boolean;
  disableRelated?: string[];
  requireRelated?: string[];
  validations?: Function[];
  validateOnChange?: boolean;
  onChange?: Function;
  onDrop?: Function;
  loading?: boolean;
  readOnly?: boolean;
  spellCheck?: boolean;
  autoComplete?: string;
  contentEditorStyles?: {};
  helperText?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
}

export interface IState {
  value?: string;
  displayedValue?: string;
  loading?: boolean;
  isValid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  errors?: string[];
  requireByRelated?: boolean;
  requireRelated?: string | string[];
  disableRelated?: string | string[];
  validateOnChange?: boolean;
  parentFormOnChange?: Function; // No initializate
}
class Wysiwyg extends Component<IProps, IState> {
  private editor = React.createRef<HTMLDivElement>();
  private inputRef: any = React.createRef<HTMLInputElement>();
  private tooltipRef = createRef<HTMLSpanElement>();
  private _isMounted: boolean = false;
  private loadingClass: string = "";
  private validationClass: string = "";
  type: string = "";
  constructor(props: IProps) {
    super(props);
    this.state = {
      value: this.props.value ? this.props.value : "",
      displayedValue: this.props.value ? this.props.value : "",
      loading: this.props.loading,
      // isValid: this.props.isValid
      disabled: this.props.disabled,
      readOnly: this.props.readOnly,
      errors: this.props.errors,
      requireByRelated: false,
      requireRelated: this.props.requireRelated,
      disableRelated: this.props.disableRelated,
      validateOnChange: this.props.validateOnChange === false ? this.props.validateOnChange : true,
    };
    this.type = "Wysiwyg";
  }

  componentDidMount() {
    try {
      document?.execCommand("defaultParagraphSeparator", false, "p");
    } catch (error) {}

    this._isMounted = true;
    if (this.context && typeof this.context.addInputToContext === "function") {
      this.context.addInputToContext(this);
    }
    this.setState((state, props) => {
      const value = props.value !== undefined ? props.value : state.value;
      return { value, displayedValue: value };
    });
  }
  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (this._isMounted) {
      if (this.props.value !== prevProps.value) {
        this.setState({ value: this.props.value, displayedValue: this.props.value });
      }
      if (this.props.readOnly !== prevProps.readOnly) {
        this.setState({ readOnly: this.props.readOnly });
      }
      if (this.props.loading !== prevProps.loading) {
        this.setState({ loading: this.props.loading });
      }
      if (this.props.disabled !== prevProps.disabled) {
        this.setState({ disabled: this.props.disabled });
      }
      if (this.props.disableRelated !== prevProps.disableRelated) {
        this.setState({ disableRelated: this.props.disableRelated });
      }
      if (this.props.requireRelated !== prevProps.requireRelated) {
        this.setState({ requireRelated: this.props.requireRelated });
      }

      try {
        if (JSON.stringify(prevState) !== JSON.stringify(this.state)) {
          //just for you sonar =),  to avoid wrong code smell for prevState, needed for checkbox input
        }
      } catch (error) {}
    }
  }

  processChange = (value: string, e: SyntheticEvent | any = undefined) => {
    if (e?.persist) e.persist();
    this.setState({ value: value }, async () => {
      if (this.state.validateOnChange && !e?.target?.fakeEvent) {
        validateInput(this, this.context);
      }
      if (e && this.props.onChange) {
        this.props.onChange(e);
      }

      if (e && !e?.target?.fakeEvent && typeof this.state.parentFormOnChange === "function") {
        this.state.parentFormOnChange(e);
      }
    });
  };

  // Sonar: This is called in other part of the code
  change = (value: string, forceUserEvent: boolean | undefined = false) => {
    const isFakeEvent = forceUserEvent ? false : true;
    const fakeEvent = {
      target: { ...this.inputRef, value: value, fakeEvent: isFakeEvent },
    };
    this.setState({ displayedValue: value });
    this.processChange(value, fakeEvent);
  };

  onCommandChange = (e: any) => {
    const _e = { target: { innerHTML: this.editor?.current?.innerHTML } } as any;
    this.onChange(_e);
  };

  onDrop = (e: any) => {
    this.props.onDrop && this.props.onDrop(e);
  };
  onDragOver = (e: any) => {
    const _e = e as Event;
    if (this.props.onDrop) {
      _e.stopPropagation();
      _e.preventDefault();
    }
  };

  onCopy = (e: any) => {
    e.preventDefault();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0).cloneContents();
    const tempDiv = document.createElement("div");
    tempDiv.appendChild(range);

    let html = tempDiv.innerHTML; // Maintains structure and format
    html = this.cleanHtml(html); // Clean up unnecessary HTML

    e.clipboardData.setData("text/plain", html);
  };

  cleanHtml = (html: string) => {
    // Word can provide a complete HTML document instead of a fragment.
    if (/<html[\s>]/i.test(html)) {
      html = new DOMParser().parseFromString(html, "text/html").body.innerHTML;
    }

    html = html.replace(/<!--\[if[\s\S]*?endif\]-->/gi, "");

    // Replace non-breaking spaces with regular spaces
    html = html.replace(/&nbsp;/g, " ");
    // Remove empty <p> and <br> tags
    html = html.replace(/<p>\s*<\/p>/g, "");

    // Preserve list structure
    // First, temporarily replace list items with a special marker
    html = html.replace(/<li>([\s\S]*?)<\/li>/g, "{{LI_START}}$1{{LI_END}}");

    // Then handle the list structure
    html = html.replace(/<p>\s*<ul>([\s\S]*?)<\/ul>\s*<\/p>/g, "<ul>$1</ul>");
    html = html.replace(/<p>\s*<ol>([\s\S]*?)<\/ol>\s*<\/p>/g, "<ol>$1</ol>");

    // Restore list items
    html = html.replace(/{{LI_START}}([\s\S]*?){{LI_END}}/g, "<li>$1</li>");

    // Handle bullet points (lines starting with -)
    html = html.replace(/<p>\s*-\s*([\s\S]*?)\s*<\/p>/g, "<ul><li>$1</li></ul>");

    // Preserve Word's inline formatting before sanitizing styles, including multi-line content.
    const formattedContent = document.createElement("div");
    formattedContent.innerHTML = html;
    formattedContent.querySelectorAll<HTMLElement>("span[style], div[style]").forEach((element) => {
      const { fontWeight, fontStyle, textDecoration, textDecorationLine } = element.style;
      const tags = [];
      if (["bold", "600", "700"].includes(fontWeight)) tags.push("b");
      if (fontStyle === "italic") tags.push("i");
      if (`${textDecoration} ${textDecorationLine}`.includes("underline")) tags.push("u");
      tags.forEach((tag) => {
        const wrapper = document.createElement(tag);
        while (element.firstChild) wrapper.appendChild(element.firstChild);
        element.appendChild(wrapper);
      });
    });
    html = formattedContent.innerHTML;

    // Move floating <b> inside the previous <p> if needed
    html = html.replace(/<\/p>\s*<b>([\s\S]*?)<\/b>\s*<p>/g, " <b>$1</b> ");

    // Remove nested <p> while preserving content
    html = html.replace(/<p>\s*<p>/g, "<p>").replace(/<\/p>\s*<\/p>/g, "</p>");

    // Ensure proper spacing between elements
    html = html.replace(/<\/p>\s*<p>/g, "</p>\n<p>");
    html = html.replace(/<\/ul>\s*<p>/g, "</ul>\n<p>");
    html = html.replace(/<\/p>\s*<ul>/g, "</p>\n<ul>");

    const cleanHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "ul", "ol", "li", "br", "div", "a", "p"],
      ALLOWED_ATTR: ["href"],
    });

    return cleanHtml;
  };

  normalizeContent = (html: string): string => {
    // Remove unnecessary spaces and extraneous characters
    html = this.escapeEspecialChars(html);

    if (/<html[\s>]/i.test(html)) {
      html = new DOMParser().parseFromString(html, "text/html").body.innerHTML;
    }

    // Ensure content is wrapped in <p> if it isn't
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html.trim();

    let newHtml = Array.from(tempDiv.childNodes)
      .map((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
          // Check if the text starts with a bullet point
          const text = node.textContent.trim();
          if (text.startsWith("-")) {
            return `<ul><li>${text.substring(1).trim()}</li></ul>`;
          }
          return `<p>${text}</p>`;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          // Preserve list structure
          if (element.tagName === "UL" || element.tagName === "OL") {
            return element.outerHTML;
          }
          return element.outerHTML;
        }
        return "";
      })
      .join("");

    newHtml = this.cleanHtml(newHtml);
    newHtml = this.fixSpacingInTags(newHtml);
    return newHtml;
  };

  fixSpacingInTags = (html: string) => {
    // Agregar espacios entre etiquetas <b>, <i> y palabras adyacentes
    return html.replace(/<\/(b|i)>(\S)/g, "</$1> $2").replace(/(\S)(<(b|i)>)/g, "$1 $2");
  };

  onChange = async (e: SyntheticEvent) => {
    const target = (e.currentTarget || e.target) as HTMLDivElement;
    let newValue = this.normalizeContent(target.innerHTML); // Normalize the content

    if (e.persist) e.persist();
    this.processChange(newValue, e);
  };

  onPaste = (e: any) => {
    e.preventDefault();
    let text = "";
    const _window: any = window;
    const clipBoardData: any = _window?.clipboardData;

    if (e.clipboardData || e.originalEvent?.clipboardData) {
      text = (e.originalEvent || e).clipboardData.getData("text/html") || (e.originalEvent || e).clipboardData.getData("text/plain");
    } else if (clipBoardData) {
      text = clipBoardData.getData("Text");
    }

    text = this.escapeEspecialChars(text);
    text = this.normalizeContent(text);

    document.execCommand("insertHTML", false, text);
    // Save the whole editor, including the content surrounding the pasted fragment.
    if (this.editor.current) {
      this.processChange(this.normalizeContent(this.editor.current.innerHTML), e);
    }
  };

  escapeEspecialChars(text: string = ""): string {
    text = text.replace(bytesControlCharactersRegExp, '');
    text = text.replace(literalControlCharactersRegExp, '');
    text = text.replaceAll(htmlStrangeCharacters, "");
    return text.replaceAll(htmlExtraSpacesRegExp, htmlOnlyOneExtraSpace);
  }

  // Renders
  renderErrorMessage = () => {
    if (this.state.errors) {
      return <div className="input-error">{this.state.errors[0]}</div>;
    } else if (this.props.helperText) {
      return (
        <div className="input-helper-text">
          <span className="material-icons info-icon mr1" style={{ color: "#1c304b" }}>
            info
          </span>
          {this.props.helperText}
        </div>
      );
    }
  };

  processCSSClasses = () => {
    this.validationClass = "";
    if (typeof this.state.isValid !== "undefined" && this.state.isValid !== null) {
      this.validationClass = this.state.isValid ? "valid" : "error";
    }
    if (this.props.validations) {
      this.props.validations.forEach((element) => {
        this.validationClass += " validation-" + element.name;
      });
    }
    if ((this.props.validations && this.props.validations.indexOf(required) !== -1) || this.state.requireByRelated) {
      this.validationClass += " validation-required";
    }
    this.loadingClass = this.state.loading ? "loading" : "";
  };

  renderHiddenInput() {
    return (
      <div className="input-container hidden">
        <input
          id="reactInput"
          type="hidden"
          ref={this.inputRef}
          disabled={this.state.disabled}
          readOnly={this.state.readOnly}
          placeholder={this.props.placeholder}
          name={getMultilanguageInputName(this)}
          value={this.state.value ? this.state.value : ""}
        />
      </div>
    );
  }

  renderTooltip(parentRef?: any) {
    if (this.props.tooltip) {
      return (
        <Tooltip parentRef={parentRef} renderAsPortal>
          {this.props.tooltip}
        </Tooltip>
      );
    }
  }

  renderTooltipIcon() {
    return this.props.tooltip ? (
      <span className="material-icons info-icon" ref={this.tooltipRef}>
        info
        {this.renderTooltip(this.tooltipRef)}
      </span>
    ) : null;
  }

  onFocus = () => {
    const _self = this;
    const value = _self.state.value;
    if (!value) {
      try {
        if (!document.queryCommandState("insertOrderedList") && !document.queryCommandState("insertUnorderedList")) {
          document?.execCommand("formatBlock", false, "p"); // Keep paragraphs without interrupting an active list.
        }
        //also prevents to nest <p><p></p></p>
      } catch (error) {}
    }
  };

  getCharacterCount = (): number => {
    return (this.state.value || "").length;
  };

  renderCharacterCount = () => {
    if (this.props.showCharacterCount && this.props.maxLength) {
      const currentCount = this.getCharacterCount();
      return (
        <div className="text_right label-text mt1">
          {currentCount}/{this.props.maxLength}
        </div>
      );
    }
    return null;
  };

  render() {
    this.processCSSClasses();
    return (
      <div
        onDrop={this.onDrop}
        className={`input-wrapper ${this.props.readOnly || this.state.disabled ? "input-wrapper_disabled" : ""} ${this.validationClass} ${this.loadingClass} ${this.props.className}`}
      >
        {this.props.label ? (
          <label className="caption mb1" htmlFor={this.props.id}>
            {this.validationClass && (this.validationClass.includes("validation-required") || this.validationClass.includes("required")) && <small className="required">*</small>}
            {this.props.label} {this.renderTooltipIcon()}
          </label>
        ) : null}
        {renderMutilanguageDefaultHiddenInput(this)}
        <div className="editor-container">
          <WysiwygBarCommands
            emptyFormat={this.props.emptyFormat}
            onCommandChange={this.onCommandChange}
            customFunction={this.props.customFunction}
            editor={this.editor?.current}
            undo={this.props.undo}
            redo={this.props.redo}
            headers={this.props.headers}
            bold={this.props.bold}
            italic={this.props.italic}
            underline={this.props.underline}
            strikeThrough={this.props.strikeThrough}
            justifyCenter={this.props.justifyCenter}
            justifyLeft={this.props.justifyLeft}
            justifyRight={this.props.justifyRight}
            justifyFull={this.props.justifyFull}
            unorderedList={this.props.unorderedList}
            orderedList={this.props.orderedList}
            indent={this.props.indent}
            outdent={this.props.outdent}
            table={this.props.table}
            link={this.props.link}
            unlink={this.props.unlink}
            image={this.props.image}
          />
          {this.renderHiddenInput()}
          <div
            spellCheck={this.props.spellCheck ?? false}
            ref={this.editor}
            id={this.props.id}
            data-testid={this.props.id}
            dangerouslySetInnerHTML={{ __html: this.state.displayedValue ? this.state.displayedValue : "" }}
            onDrop={this.onDrop}
            onDragOver={this.onDragOver}
            onCopy={(e) => this.onCopy(e)}
            onPaste={this.onPaste}
            onInput={this.onChange}
            onFocus={this.onFocus}
            contentEditable={!this.state.readOnly && !this.state.disabled}
            style={this.props.contentEditorStyles}
            className={"editor-content editor-container-child " + (this.props.emptyFormat ? " empty-border" : "")}
          ></div>
        </div>
        {this.renderCharacterCount()}
        {this.renderErrorMessage()}
      </div>
    );
  }
}

Wysiwyg.contextType = FormContext;
export default Wysiwyg;
